import type { RodoActivity, RodoMain, RodoProcessor } from './rodo.ts';

export type Cycle = 'monthly' | 'annual' | 'usage' | 'irregular';
export type Timing = 'prepaid' | 'arrears';
export type VatIdSet = 'yes' | 'no' | 'unknown';
export type InvoiceStatus = 'draft' | 'new' | 'sent' | 'booked';
export type Repeat = 'none' | 'monthly' | 'yearly';

/** Zagraniczny dostawca usług (import usług, odwrotne obciążenie). */
export interface Service {
  name: string;
  vendor?: string;
  country?: string;
  vendorVat?: string;
  currency?: string;
  vatRate?: number;
  cycle?: Cycle;
  timing?: Timing;
  vatIdSet?: VatIdSet;
  /** YYYY-MM – od kiedy sprawdzać brakujące faktury */
  since?: string;
  invoiceSource?: string;
  /** Adresy lub domeny nadawców maili z fakturami, oddzielone przecinkami */
  senders?: string;
  notes?: string;
  active?: boolean;
  verify?: boolean;
  /** Płacę kartą automatycznie w dniu faktury – bez potwierdzenia zapłaty data zapłaty = data wystawienia. */
  cardAutopay?: boolean;
}

export interface Invoice {
  /** Usługa z listy; pusty – „Inne”: jednorazowy dostawca opisany polami vendor i country. */
  serviceId: string;
  /** Dostawca faktury bez usługi (przy fakturze z usługą bierzemy dane z usługi). */
  vendor?: string;
  /** Kraj dostawcy faktury bez usługi, kod ISO z dwóch liter. */
  country?: string;
  number?: string;
  issueDate?: string;
  periodFrom?: string;
  periodTo?: string;
  paidDate?: string;
  tpManual?: boolean;
  taxPoint?: string;
  currency?: string;
  amount?: number | string | null;
  vatRate?: number;
  rate?: number | null;
  rateDate?: string;
  rateTable?: string;
  status?: InvoiceStatus;
  note?: string;
  vatCharged?: number;
  /** Plik na Dysku Google */
  fileId?: string;
  fileName?: string;
  fileLink?: string;
  /** Wiadomość Gmail, z której pochodzi faktura */
  gmailId?: string;
  createdAt?: string;
  updatedAt?: string;
  sentAt?: string;
  /** Pola wypełnione z PDF i jeszcze niesprawdzone (podświetlane w formularzu). */
  extracted?: string[];
}

export interface CustomDeadline {
  title: string;
  date: string;
  repeat: Repeat;
  note?: string;
}

export interface Rate {
  cur: string;
  date: string;
  mid: number;
  table?: string;
}

export interface Toggles {
  /** Przelew VAT do 25. (deklarację JPK_V7M składa biuro) */
  vat: boolean;
  docs: boolean;
  vatue: boolean;
  oss: boolean;
  bdo: boolean;
  ksef: boolean;
  infakt: boolean;
  rodo: boolean;
}

/** Rodzaj dokumentu wystawianego co miesiąc w inFakcie. */
export type InfaktKind = 'invoice' | 'internal_invoice' | 'internal_evidence';

export interface InfaktLine {
  name: string;
  unit?: string;
  /** Domyślna ilość (np. godziny) */
  quantity?: number;
  /** Cena jednostkowa w zł – netto albo brutto zależnie od priceMode */
  price?: number;
  priceMode?: 'net' | 'gross';
  /** Stawka VAT: 23, 8, 5, 0, zw, np */
  vat: string;
  /** Stawka ryczałtu, np. 12, 8.5, 5.5, 3 */
  flat: string;
  pkwiu?: string;
  /** Co wpisujesz co miesiąc: ilość (godziny), kwotę albo nic (stała pozycja) */
  variable?: 'quantity' | 'amount' | 'none';
}

/** `classification_symbol` dowodu wewnętrznego w inFakt API */
export type EvidenceClassification = 'salary' | 'sold';
/** `transaction_kind_symbol` faktury wewnętrznej w inFakt API */
export type InternalInvoiceKind = 'nonaccounting_sales' | 'free_transfer_of_goods' | 'inventory_for_vat' | 'refund_cash_register_tax_relief' | 'car_purchase_from_eu';

export interface InfaktTemplate {
  kind: InfaktKind;
  name: string;
  clientId?: number;
  clientName?: string;
  clientNip?: string;
  /** Dowód wewnętrzny: klasyfikacja przychodu w inFakcie (domyślnie pozostałe przychody). */
  classification?: EvidenceClassification;
  /** Faktura wewnętrzna: rodzaj transakcji w inFakcie (domyślnie sprzedaż bezrachunkowa). */
  transactionKind?: InternalInvoiceKind;
  lines: InfaktLine[];
  paymentMethod?: string;
  paymentDays?: number;
  notes?: string;
  active?: boolean;
}

/** Dokument z szablonu za dany miesiąc; id: `${templateId}_${RRRR-MM}` */
export interface InfaktMonthDoc {
  templateId: string;
  ym: string;
  status: 'created' | 'found' | 'manual' | 'pending' | 'error';
  values?: number[];
  number?: string;
  uuid?: string;
  ref?: string;
  net?: number;
  at: string;
  message?: string;
}

export interface Settings {
  companyName: string;
  brand: string;
  shop: string;
  nip: string;
  regon: string;
  address: string;
  taxOffice: string;
  microAccount: string;
  accountantName: string;
  accountantEmail: string;
  accountantPhone: string;
  docsDay: number;
  leadDays: number;
  /** YYYY-MM – od kiedy pilnować brakujących faktur */
  trackFrom: string;
  /** YYYY-MM-DD – terminy przed tą datą uznaję za zamknięte */
  startDate: string;
  toggles: Toggles;
  ksefChecks: Record<string, boolean>;
  calendarSync: boolean;
  gmailSync: boolean;
}

export const DEFAULT_SETTINGS: Settings = {
  companyName: '',
  brand: '',
  shop: '',
  nip: '',
  regon: '',
  address: '',
  taxOffice: '',
  microAccount: '',
  accountantName: '',
  accountantEmail: '',
  accountantPhone: '',
  docsDay: 5,
  leadDays: 3,
  trackFrom: '2026-09',
  startDate: '2026-10-23',
  toggles: { vat: true, docs: true, vatue: false, oss: false, bdo: true, ksef: true, infakt: true, rodo: true },
  ksefChecks: {},
  calendarSync: true,
  gmailSync: true,
};

export interface DoneMark {
  at: string;
}

export interface Data {
  services: Record<string, Service>;
  invoices: Record<string, Invoice>;
  custom: Record<string, CustomDeadline>;
  done: Record<string, DoneMark>;
  rates: Record<string, Rate>;
  settings: { main?: Partial<Settings> };
  infaktTemplates: Record<string, InfaktTemplate>;
  infaktDocs: Record<string, InfaktMonthDoc>;
  rodo: { main?: RodoMain };
  rodoActivities: Record<string, RodoActivity>;
  rodoProcessors: Record<string, RodoProcessor>;
  /** Uzupełnienia do wydruku dokumentów wewnętrznych, których PDF-u nie oddał inFakt; id: uuid dokumentu w inFakcie. */
  printData: Record<string, PrintData>;
}

/** Dane do wydruku dokumentu wewnętrznego: nazwa i stawka ryczałtu każdej pozycji (pusta wartość = dane z inFaktu). */
export interface PrintData {
  kind: 'internal_invoice' | 'internal_evidence';
  number: string;
  lines: { name: string; flat: string }[];
  updatedAt: string;
}

export const COLLECTIONS = [
  'services', 'invoices', 'custom', 'done', 'rates', 'settings', 'infaktTemplates', 'infaktDocs', 'rodo', 'rodoActivities', 'rodoProcessors', 'printData',
] as const;
export type Collection = (typeof COLLECTIONS)[number];

export function mergeSettings(s?: Partial<Settings> | null): Settings {
  const v = s || {};
  return {
    ...DEFAULT_SETTINGS,
    ...v,
    toggles: { ...DEFAULT_SETTINGS.toggles, ...(v.toggles || {}) },
    ksefChecks: { ...(v.ksefChecks || {}) },
  };
}

export function emptyData(): Data {
  return { services: {}, invoices: {}, custom: {}, done: {}, rates: {}, settings: {}, infaktTemplates: {}, infaktDocs: {}, rodo: {}, rodoActivities: {}, rodoProcessors: {}, printData: {} };
}
